import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTableSearch } from '../../hooks/useTableSearch';
import PageHeader from '../../components/PageHeader/PageHeader';
import { Box, Paper } from '@mui/material';
import { useGridApiRef, GridSortModel } from '@mui/x-data-grid-premium';
import StyledDataGrid from '../../components/DataGrid/DataGridStyles';
import { useCellUndoRedo } from '../../components/UndoRedo/useCellUndoRedo';
import Toolbar from '../../components/Toolbar/Toolbar';
import { useViewButtons } from '../../components/Views/useViewButtons';
import SaveViewDialog from '../../components/Views/SaveViewDialog';
import SavedViewsPanel from '../../components/Views/SavedViewsPanel';
import ViewContextMenu from '../../components/Views/ViewContextMenu';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';
import { DataFeedRowData } from '../../types/generatedTypes';
import { useDanelData } from './hooks/useDanelData';
import { useIllioUpload } from './hooks/useIllioUpload';
import { useUploadErrors } from './hooks/useUploadErrors';
import ErrorDetailsModal from '../../components/Modal/ErrorDetailsModal/ErrorDetailsModal';
import { useVaultUpload } from '../../hooks/useVaultUpload';
import { useToolbarConfiguration } from './hooks/useToolbarConfiguration';
import { useDanelColumns } from './hooks/useDanelColumns';
import { useViewManagement } from '../../hooks/useViewManagement/useViewManagement';
import Actions from '../../store/Actions';
import { GridControls } from '../../components/DataFeed/GridControls/GridControls';
import { ERROR_HIGHLIGHT_STYLE } from './utils/danelUtils';
import { PAGE_TYPES } from '../../constants/pageTypes';
import { processRowDataUpdate, processCellEditCommit } from './utils/gridUtils';
import DanelFileUploadModal from '../../components/DanelFileUploadModal/DanelFileUploadModal';
import { DanelFileInfo } from '../../components/DanelFileUploadItem/types';
import DanelEmptyState from '../../components/DanelEmptyState/DanelEmptyState';
import { validateExcelFile } from '../../utils/apiUtils';
import { TemplateSelector } from '../../components/DataFeed/TemplateSelector/TemplateSelector';
import { useFilteredSelectionModel } from '../../components/Groups/gridStateHelper';
import { ViewModeToggle } from '../../components/DataFeed/ViewModeToggle/ViewModeToggle';

export const DanelPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isKasefetModalOpen, setIsKasefetModalOpen] = useState(false);

  const apiRef = useGridApiRef();

  // State to track current display data for selection handler
  const [currentDisplayData, setCurrentDisplayData] = useState<
    DataFeedRowData[]
  >([]);

  // Custom hooks for data management
  const {
    rows,
    appliedTemplateId,
    appliedTemplateConfig,
    currentFileId,
    processedFileId,
    isLoading,
    refreshData,
    updateRow,
    deleteRows,
    danelConfig,
    viewMode,
    setViewMode,
    processedRows,
    hasProcessedData,
    selectedRows,
    setSelectedRows,
    handleRowSelectionChange,
    rowsType,
  } = useDanelData({ displayData: currentDisplayData });

  const {
    errors: uploadErrors,
    isModalOpen: isErrorModalOpen,
    showErrors,
    closeModal: closeErrorModal,
  } = useUploadErrors();

  const { isUploading, uploadToIllio } = useIllioUpload(
    processedFileId,
    showErrors
  );

  const [hasUploadedFiles, setHasUploadedFiles] = useState(false);

  const {
    isProcessingFiles,
    processVaultFiles,
    clearStates: clearVaultStates,
  } = useVaultUpload();

  // Grid state management
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >({});
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [preservedSortModel, setPreservedSortModel] = useState<GridSortModel>(
    []
  );
  const [autosizeColumns, setAutosizeColumns] = useState(false);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    if (!isUploading) setSortModel(newSortModel);
  };

  const handleColumnVisibilityModelChange = (
    newModel: Record<string, boolean>
  ) => {
    setColumnVisibilityModel(newModel);
  };

  const {
    filteredData: filteredRows,
    searchTerm,
    handleSearch,
  } = useTableSearch<DataFeedRowData>(rows);

  // View management
  const viewManagement = useViewManagement({
    pageType: PAGE_TYPES.DANEL,
    apiRef,
    searchTerm,
    selectedRows,
    autosizeColumns,
    displayData: filteredRows, // Will be updated below after displayData calculation
    handleSearch,
    setSelectedRows,
    setAutosizeColumns,
  });

  // Filter data to show only selected rows if active view has selections
  const displayData = viewManagement.filteredDisplayData as DataFeedRowData[];

  // Sync currentDisplayData with displayData for selection handler
  useEffect(() => {
    setCurrentDisplayData(displayData);
  }, [displayData]);

  // Clear selections when switching view modes (raw ↔ processed)
  useEffect(() => {
    setSelectedRows([]);
  }, [viewMode, setSelectedRows]);

  // Wrapper function for upload that calls refresh
  const handleUploadToIllio = React.useCallback(
    async (rowIndices: number[]) => {
      await uploadToIllio(rowIndices);
      await refreshData();
    },
    [uploadToIllio, refreshData]
  );

  // Row operations
  const handleUploadToIllioWithSort = async (rowIndices: number[]) => {
    setPreservedSortModel([...sortModel]);
    await handleUploadToIllio(rowIndices);
    setSelectedRows([]);
    if (preservedSortModel.length > 0) {
      setSortModel([...preservedSortModel]);
      setPreservedSortModel([]);
    }
  };

  const handleDeleteRows = async (rowIds: string[]) => {
    // Filter to get the rows that match the selected IDs
    const currentDisplayedRows =
      viewMode === 'processed' ? processedRows : displayData;

    const rowIndicesToDelete = currentDisplayedRows
      .filter(row => rowIds.includes(row.id.toString()))
      .map(row => row.id.toString());

    await deleteRows(rowIndicesToDelete);
  };

  // Create filtered rowSelectionModel to preserve selections during search
  const getRowIdCallback = useCallback(
    (row: DataFeedRowData) => row.id.toString(),
    []
  );
  const getGridSelectionModel = useFilteredSelectionModel(
    selectedRows,
    displayData,
    getRowIdCallback
  );

  // Inject error highlighting CSS
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = ERROR_HIGHLIGHT_STYLE;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Reset hasUploadedFiles when data loads successfully
  useEffect(() => {
    if (hasUploadedFiles && rows.length > 0) {
      setHasUploadedFiles(false);
    }
  }, [hasUploadedFiles, rows.length, setHasUploadedFiles]);

  // Original processRowUpdate function (for undo/redo wrapping)
  const originalProcessRowUpdate = async (
    newRow: DataFeedRowData,
    oldRow: DataFeedRowData
  ) => {
    try {
      const processedRow = processRowDataUpdate(newRow, oldRow, danelConfig);
      return await updateRow(processedRow, oldRow);
    } catch {
      // Error updating cell - logged for debugging
      showSnackbar({
        title: 'Update Failed',
        type: SnackbarType.error,
        message: 'Failed to update cell data',
      });
      // Revert to the old row data on error
      return oldRow;
    }
  };

  // Handle immediate cell edit to sanitize pasted values
  const handleCellEditCommit = React.useCallback(
    (params: { id: string | number; field: string; value: unknown }) =>
      processCellEditCommit(params, rows, updateRow),
    [rows, updateRow]
  );

  // Initialize undo/redo system
  const {
    wrappedProcessRowUpdate: processRowUpdate,
    undo,
    redo,
    canUndo,
    canRedo,
    getUndoActionInfo,
    getRedoActionInfo,
  } = useCellUndoRedo({
    processRowUpdate: originalProcessRowUpdate,
    getData: () => rows,
  });

  // Generate columns using the custom hook
  const { columns } = useDanelColumns({
    rows: viewMode === 'raw' ? rows : processedRows,
    processRowUpdate,
    autosizeColumns,
    viewMode,
    appliedTemplateConfig,
    rowsType,
  });

  // Handle autosize state changes
  useEffect(() => {
    if (autosizeColumns && apiRef.current && columns.length > 0) {
      const columnFields = columns.map(col => col.field);
      apiRef.current.autosizeColumns({
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
        columns: columnFields,
      });
    } else if (!autosizeColumns && apiRef.current && columns.length > 0) {
      // Reset column widths to allow flex to work
      const columnFields = columns.map(col => col.field);
      columnFields.forEach(field => {
        apiRef.current?.setColumnWidth(field, 0);
      });
    }
  }, [autosizeColumns, apiRef, columns]);

  // Export data handler
  const handleExport = React.useCallback(() => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv();
    }
  }, []);

  const handleUploadExcel = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset so same file can be selected again
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type first
      const validation = validateExcelFile(file.name);
      if (!validation.isValid) {
        showSnackbar({
          title: 'Upload Failed',
          type: SnackbarType.error,
          message: validation.error || 'Invalid file type',
        });
        return;
      }

      try {
        await Actions.uploadDanelExcel(file);
        showSnackbar({
          title: 'Upload Successful',
          type: SnackbarType.success,
          message: 'Excel file uploaded successfully',
        });
        await refreshData();
        setViewMode('raw');
      } catch (error) {
        console.error('Error uploading file:', error);

        let errorMessage = 'Failed to upload Excel file';

        // Try to extract specific error message from backend response
        if (error && typeof error === 'object') {
          const err = error as any;
          if (err.response?.data?.detail) {
            errorMessage = err.response.data.detail;
          } else if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
          } else if (err.message) {
            errorMessage = `Failed to upload Excel file: ${err.message}`;
          }
        }

        showSnackbar({
          title: 'Upload Failed',
          type: SnackbarType.error,
          message: errorMessage,
        });
      }
    }
  };

  const handleKasefetUpload = () => {
    setIsKasefetModalOpen(true);
  };

  const handleKasefetModalClose = () => {
    setIsKasefetModalOpen(false);
  };

  const handleKasefetFileUpload = async (
    files: DanelFileInfo[],
    returnExcel: boolean = false,
    alreadySaved: boolean = false
  ) => {
    // If modal already handled the save operation, just refresh UI
    // The alreadySaved flag prevents duplicate API calls and is part of the fix for the double-download issue.
    if (alreadySaved) {
      // Always refresh data - it's saved to database regardless of Excel download option
      refreshData();
      return;
    }

    clearVaultStates();
    await processVaultFiles(files, returnExcel);
    // Always refresh data to show newly uploaded vault data (data is saved regardless of Excel download)
    refreshData();
    setViewMode('raw');
  };

  // Get view buttons from hook
  const viewButtons = useViewButtons({
    onSaveView: viewManagement.openSaveDialog,
    onOpenSavedViews: viewManagement.openSavedViewsPanel,
    loading: isUploading,
    disabled: false,
  });

  // Helper function to track view modifications
  const updateViewModifications = React.useCallback(() => {
    const modifications = viewManagement.compareWithAppliedView();
    viewManagement.setViewModifications(modifications);
  }, [viewManagement]);

  // Toolbar configuration hook
  const { toolbarButtons } = useToolbarConfiguration({
    selectedRows,
    displayData,
    processedRows,
    viewMode,
    isUploadingToIllio: isLoading || isUploading,
    onUploadExcel: handleUploadExcel,
    onUploadKasefet: handleKasefetUpload,
    onUploadToIllio: handleUploadToIllioWithSort,
    onDeleteRows: handleDeleteRows,
    viewButtons,
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        title="Data Feed"
        subtitle="Upload, manage, and export your data"
      />
      <Paper
        sx={{
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        {(displayData.length > 0 || hasUploadedFiles || searchTerm) && (
          <>
            <Toolbar
              onSearch={handleSearch}
              placeholder="Search Danel..."
              searchTerm={searchTerm}
              onExport={handleExport}
              buttons={toolbarButtons}
            />

            <TemplateSelector
              onApplySuccess={refreshData}
              initialTemplateId={appliedTemplateId}
            />

            {rows.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <ViewModeToggle
                  value={viewMode}
                  onChange={setViewMode}
                  disabled={false}
                />
              </Box>
            )}

            <GridControls
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={undo}
              onRedo={redo}
              getUndoActionInfo={getUndoActionInfo}
              getRedoActionInfo={getRedoActionInfo}
              apiRef={apiRef}
              autosizeColumns={autosizeColumns}
              onAutosizeChange={setAutosizeColumns}
              activeView={viewManagement.activeView}
              viewModifications={viewManagement.viewModifications}
              onClearActiveView={viewManagement.clearActiveView}
              onUpdateCurrentView={viewManagement.updateCurrentView}
            />
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Box
          sx={{
            flex: 1,
            minHeight: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {displayData.length === 0 &&
          !hasUploadedFiles &&
          !searchTerm &&
          rows.length === 0 ? (
            <DanelEmptyState
              onUploadSuccess={refreshData}
              onUploadExcel={handleUploadExcel}
              onUploadKasefet={handleKasefetUpload}
              disabled={isLoading || isUploading || isProcessingFiles}
            />
          ) : (
            <StyledDataGrid
              apiRef={apiRef}
              rows={viewMode === 'processed' ? processedRows : displayData}
              columns={columns}
              getRowId={getRowIdCallback}
              pageSizeOptions={[5, 10, 300]}
              checkboxSelection
              disableRowSelectionOnClick
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={() => {
                // ProcessRowUpdate error handled by component
              }}
              onCellEditCommit={handleCellEditCommit}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={model => {
                handleColumnVisibilityModelChange(model);
                updateViewModifications();
              }}
              sortModel={sortModel}
              onSortModelChange={model => {
                handleSortModelChange(model);
                updateViewModifications();
              }}
              onFilterModelChange={() => {
                updateViewModifications();
              }}
              onPinnedColumnsChange={() => {
                if (viewManagement.activeView) {
                  requestAnimationFrame(() => {
                    updateViewModifications();
                  });
                }
              }}
              pinnedColumns={{ left: ['__check__'] }}
              disableColumnSorting={isUploading}
              rowSelectionModel={getGridSelectionModel}
              onRowSelectionModelChange={handleRowSelectionChange}
              loading={
                isLoading ||
                isUploading ||
                isProcessingFiles ||
                (displayData.length === 0 && hasUploadedFiles)
              }
              slotProps={{
                loadingOverlay: {
                  variant: 'linear-progress',
                },
              }}
              sx={{
                height: '100%',
              }}
            />
          )}
        </Box>
      </Paper>

      <SaveViewDialog
        open={viewManagement.isSaveViewDialogOpen}
        onClose={viewManagement.closeSaveDialog}
        onSave={viewManagement.saveView}
        editingView={viewManagement.editingView}
        editMode={viewManagement.editMode}
        currentSearchTerm={searchTerm}
        autosizeColumns={autosizeColumns}
        selectedRows={selectedRows}
        savedViews={viewManagement.savedViews}
        columnFilters={viewManagement.getColumnFilters()}
        sortModel={viewManagement.getSortModel()}
        columnVisibility={viewManagement.getColumnVisibility()}
        pinnedColumns={viewManagement.getPinnedColumns()}
        columns={columns}
      />

      <SavedViewsPanel
        views={viewManagement.savedViews}
        onViewClick={viewManagement.handleViewClick}
        onSaveClick={viewManagement.openSaveDialog}
        onMenuClick={viewManagement.handleMenuClick}
        activeView={viewManagement.activeView}
        open={viewManagement.isSavedViewsPanelOpen}
        onClose={viewManagement.closeSavedViewsPanel}
      />

      <ViewContextMenu
        anchorEl={viewManagement.contextMenuAnchor}
        open={Boolean(viewManagement.contextMenuAnchor)}
        onClose={viewManagement.handleCloseContextMenu}
        onApply={viewManagement.handleApplyView}
        onRename={viewManagement.handleRenameView}
        onUpdate={viewManagement.handleUpdateView}
        onDelete={viewManagement.handleDeleteView}
      />

      <DanelFileUploadModal
        open={isKasefetModalOpen}
        onClose={handleKasefetModalClose}
        onUpload={handleKasefetFileUpload}
        disabled={isUploading || isProcessingFiles}
      />

      <ErrorDetailsModal
        open={isErrorModalOpen}
        onClose={closeErrorModal}
        title="Upload Errors"
        errors={uploadErrors}
      />
    </Box>
  );
};
